import {
  Deficiency,
  deficiencyAction,
  DeficiencySortByFilter,
  IViewInspectorDocument,
} from '../models/models';
import { InspectionList as InspectionListType } from '../rxdb/inspectionList/queryBuilder';
import { PmCheckList, PmCheckListChildren } from '../rxdb/pmCheckList/queryBuilder';
import { RiskAssessment } from './enums';
import {
  generateActionsList,
  getStatusForDeficiency,
  getTagForActiveDeficieny,
  removalCategoryIds,
} from './utilityFunctions';

export function uniq<T>(array: T[]): T[] {
  return Array.from(new Set(array));
}

export const sortByStepNo = (
  a: PmCheckList | PmCheckListChildren,
  b: PmCheckList | PmCheckListChildren,
) => {
  return a.stepNo < b.stepNo ? -1 : 1;
};

export const deficiencySortHelper = (
  a: Deficiency,
  b: Deficiency,
  {
    name,
    areaId,
    urgency,
    componentType,
    actionType,
    status,
    removedFromPlanning,
    remedialAction,
    conditionRating,
    dateObserved,
    repairFactor,
  }: DeficiencySortByFilter,
  isActiveDeficiency: boolean,
) => {
  let res;
  res =
    name === 'asc' ? (a.name < b.name ? -1 : 1) : name === 'desc' ? (a.name > b.name ? -1 : 1) : '';
  if (res !== -1 && res !== 1) {
    res =
      areaId === 'asc'
        ? a.componentAreaInfo[0].areaPcsId < b.componentAreaInfo[0].areaPcsId
          ? -1
          : 1
        : areaId === 'desc'
        ? a.componentAreaInfo[0].areaPcsId > b.componentAreaInfo[0].areaPcsId
          ? -1
          : 1
        : '';
  }
  if (res !== -1 && res !== 1) {
    res =
      urgency === 'asc'
        ? a?.inspectorUrgency < b?.inspectorUrgency
          ? -1
          : 1
        : urgency === 'desc'
        ? a?.inspectorUrgency > b?.inspectorUrgency
          ? -1
          : 1
        : '';
  }
  if (res !== -1 && res !== 1) {
    res =
      componentType === 'asc'
        ? a.componentType < b.componentType
          ? -1
          : 1
        : componentType === 'desc'
        ? a.componentType > b.componentType
          ? -1
          : 1
        : '';
  }
  if (res !== -1 && res !== 1) {
    res =
      remedialAction === 'asc'
        ? a.remedialAction < b.remedialAction
          ? -1
          : 1
        : remedialAction === 'desc'
        ? a.remedialAction > b.remedialAction
          ? -1
          : 1
        : '';
  }
  if (res !== -1 && res !== 1) {
    const ratingIdA = a.riskAssessment?.find(rat => rat.name === RiskAssessment.RATING);
    const ratingIdB = b.riskAssessment?.find(rat => rat.name === RiskAssessment.RATING);
    res =
      conditionRating === 'asc'
        ? ratingIdA?.componentRiskCategoryRankType < ratingIdB?.componentRiskCategoryRankType
          ? -1
          : 1
        : conditionRating === 'desc'
        ? ratingIdA?.componentRiskCategoryRankType > ratingIdB?.componentRiskCategoryRankType
          ? -1
          : 1
        : '';
  }
  if (res !== -1 && res !== 1) {
    res =
      repairFactor === 'asc'
        ? a.repairFactorId < b.repairFactorId
          ? -1
          : 1
        : repairFactor === 'desc'
        ? a.repairFactorId > b.repairFactorId
          ? -1
          : 1
        : '';
  }
  if (res !== -1 && res !== 1) {
    const defAId =
      !a.isActive &&
      removalCategoryIds.includes(a?.deficiencyComponentRemoval?.componentRemovalIdBySC)
        ? a?.deficiencyComponentRemoval?.componentRemovalIdBySC
        : 0;
    const defBId =
      !b.isActive &&
      removalCategoryIds.includes(b?.deficiencyComponentRemoval?.componentRemovalIdBySC)
        ? b?.deficiencyComponentRemoval?.componentRemovalIdBySC
        : 0;
    res =
      removedFromPlanning === 'asc'
        ? defAId < defBId
          ? -1
          : 1
        : removedFromPlanning === 'desc'
        ? defAId > defBId
          ? -1
          : 1
        : '';
  }
  if (res !== -1 && res !== 1) {
    const statusA = getStatusForDeficiency(
      a.deficiencyActionedList?.componentFailureStatus,
      a.deficiencyActionedList?.workPackageId,
    );
    const statusB = getStatusForDeficiency(
      b.deficiencyActionedList?.componentFailureStatus,
      b.deficiencyActionedList?.workPackageId,
    );
    res =
      status === 'asc'
        ? statusA < statusB
          ? -1
          : 1
        : status === 'desc'
        ? statusA > statusB
          ? -1
          : 1
        : '';
  }
  if (res !== -1 && res !== 1) {
    const deficiencyActionedListA = generateActionsList(a.deficiencyActionedList);
    const deficiencyActionedListB = generateActionsList(b.deficiencyActionedList);
    const hasActionIdA = deficiencyActionedListA.some((action: deficiencyAction) => action.actionId)
      ? 1
      : 0;
    const hasActionIdB = deficiencyActionedListB.some((action: deficiencyAction) => action.actionId)
      ? 1
      : 0;

    res =
      actionType === 'asc'
        ? hasActionIdA && !hasActionIdB
          ? -1
          : 1
        : actionType === 'desc'
        ? !hasActionIdA && hasActionIdB
          ? -1
          : 1
        : '';

    if (hasActionIdA && hasActionIdB) {
      const sortActionedList = (list: deficiencyAction[]): deficiencyAction[] => {
        return list.sort((x: deficiencyAction, y: deficiencyAction) => {
          const actionTypeX = x.actionType.toUpperCase();
          const actionTypeY = y.actionType.toUpperCase();

          const hasActionIdX = x.actionId ? 1 : 0;
          const hasActionIdY = y.actionId ? 1 : 0;

          if (hasActionIdX && !hasActionIdY) return -1;
          if (!hasActionIdX && hasActionIdY) return 1;

          return actionTypeX > actionTypeY ? -1 : actionTypeX < actionTypeY ? 1 : 0;
        });
      };

      const actionTypeA = sortActionedList(deficiencyActionedListA);
      const actionTypeB = sortActionedList(deficiencyActionedListB);

      res =
        actionType === 'asc'
          ? actionTypeA[0].actionType < actionTypeB[0].actionType
            ? -1
            : 1
          : actionType === 'desc'
          ? actionTypeA[0].actionType > actionTypeB[0].actionType
            ? -1
            : 1
          : '';
    }
  }
  if (res !== -1 && res !== 1) {
    res =
      dateObserved === 'asc'
        ? a.dateObserved < b.dateObserved
          ? -1
          : 1
        : dateObserved === 'desc'
        ? a.dateObserved > b.dateObserved
          ? -1
          : 1
        : '';
  }
  if (res !== -1 && res !== 1) {
    res = a.componentFailureId < b.componentFailureId ? -1 : 1;
  }
  if (
    isActiveDeficiency &&
    !name &&
    !areaId &&
    !urgency &&
    !componentType &&
    !actionType &&
    !status &&
    !removedFromPlanning &&
    !remedialAction &&
    !conditionRating &&
    !dateObserved &&
    !repairFactor
  ) {
    const getTag = (deficiency: Deficiency) => {
      return getTagForActiveDeficieny(
        deficiency.deficiencyActionedList,
        deficiency.deficiencyComponentRemoval,
        deficiency,
      );
    };

    const tagOrder = {
      'Re-Inspected': 1,
      '': 2,
      'Removed From Planning': 3,
    };

    res = tagOrder[getTag(a)] - tagOrder[getTag(b)];
  }

  return res;
};
export const inspectionListSort = (
  a: InspectionListType,
  b: InspectionListType,
  inspectionId: string,
  inspectionName: string,
  needAttention: string,
  inspectionLevel: string,
) => {
  let res;
  res =
    inspectionName === 'asc'
      ? a.assetName < b.assetName
        ? -1
        : 1
      : inspectionName === 'desc'
      ? a.assetName > b.assetName
        ? -1
        : 1
      : '';
  if (res !== -1 && res !== 1) {
    res =
      inspectionId === 'asc'
        ? Number(a.inspectionId) < Number(b.inspectionId)
          ? -1
          : 1
        : inspectionId === 'desc'
        ? Number(a.inspectionId) > Number(b.inspectionId)
          ? -1
          : 1
        : '';
  }
  if (res !== -1 && res !== 1) {
    res =
      inspectionLevel === 'asc'
        ? a.assetInspectionType < b.assetInspectionType
          ? -1
          : 1
        : inspectionLevel === 'desc'
        ? a.assetInspectionType > b.assetInspectionType
          ? -1
          : 1
        : '';
  }
  if (res !== -1 && res !== 1) {
    res =
      a.assetInspectionStatusId < b.assetInspectionStatusId
        ? -1
        : a.assetInspectionStatusId === b.assetInspectionStatusId
        ? a.inspectionId < b.inspectionId
          ? -1
          : 1
        : 1;
  }
  return res;
};

export const sortByInspectorName = (a: IViewInspectorDocument, b: IViewInspectorDocument) => {
  const nameA = a.clerkName?.toLowerCase() || a.fieldInspectorName?.toLowerCase();
  const nameB = b.clerkName?.toLowerCase() || b.fieldInspectorName?.toLowerCase();

  if (nameA < nameB) return -1;
  if (nameA > nameB) return 1;
  return 0;
};

export const sortHelper = () => {
  return {
    deficiencySortHelper,
    inspectionListSort,
  };
};
